import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["calendarPage", "id", "onCalendarPageChange", "today", "value", "maxValidationMessage", "minValidationMessage", "onChange"];
import styled from 'foundations-theming/utils';
import { isValid, toMoment } from '../../utils/SimpleDate';
import unescapedText from 'I18n/utils/unescapedText';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { IconButton } from '../IconButton/IconButton';
import { getLocaleData } from '../../utils/Locale';
import { getPageFromDate, nextCalendarPage, prevCalendarPage } from '../../utils/calendarUtils';
import { Month } from './Month';
import { StyledButton, StyledButtonTextContainer } from '../Button/PrivateButton';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "CalendarPage__Wrapper",
  componentId: "h5w8b4-0"
})(() => {
  return {
    display: 'inline-block',
    [`${StyledButton}`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    [`${StyledButtonTextContainer}`]: {
      inlineSize: '22px',
      blockSize: '22px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        inlineSize: '22px',
        blockSize: '22px'
      }
    }
  };
});

/**
 * @param value The current selected date, or null
 * @returns A string for announcing the selected date to screen readers
 */
const getAlertText = value => value ? unescapedText('ui.datePicker.dateSelected', {
  date: toMoment(value).format('LL')
}) : unescapedText('ui.datePicker.noDateSelected');
const getCalendarPage = (calendarPage, value, today) => {
  if (calendarPage) return calendarPage;
  if (isValid(value)) return getPageFromDate(value);
  return getPageFromDate(today);
};
export const CalendarPage = props => {
  const {
      calendarPage,
      id,
      onCalendarPageChange,
      today,
      value,
      maxValidationMessage,
      minValidationMessage,
      onChange
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const localeData = getLocaleData();
  const currentCalendarPage = getCalendarPage(calendarPage, value, today);
  if (!currentCalendarPage) {
    return null;
  }
  const {
    month,
    year
  } = currentCalendarPage;
  return /*#__PURE__*/_jsxs(Wrapper, {
    id: id,
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      "aria-live": "polite",
      "aria-atomic": true,
      children: getAlertText(value)
    }), /*#__PURE__*/_jsx(Month, Object.assign({}, rest, {
      onChange: onChange,
      month: month,
      maxValidationMessage: maxValidationMessage,
      minValidationMessage: minValidationMessage,
      leftControl: /*#__PURE__*/_jsx(IconButton, {
        "data-action": "prev-month",
        use: "transparent",
        "aria-label": unescapedText('ui.datePicker.prevMonth'),
        onClick: () => {
          if (!onCalendarPageChange) return;
          onCalendarPageChange(syntheticEvent(prevCalendarPage(currentCalendarPage)));
        },
        children: /*#__PURE__*/_jsx(LeftIcon, {})
      }),
      ownerId: id,
      rightControl: /*#__PURE__*/_jsx(IconButton, {
        "data-action": "next-month",
        use: "transparent",
        "aria-label": unescapedText('ui.datePicker.nextMonth'),
        onClick: () => {
          if (!onCalendarPageChange) return;
          onCalendarPageChange(syntheticEvent(nextCalendarPage(currentCalendarPage)));
        },
        children: /*#__PURE__*/_jsx(RightIcon, {
          size: "800"
        })
      }),
      today: today,
      weekdayHeadings: localeData.weekdaysShort(),
      weekStartDay: localeData.firstDayOfWeek(),
      value: value,
      year: year
    }))]
  });
};